import React from 'react';
import Modal from 'react-modal';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/solid';
import SubmitButton from './SubmitButton';
import Label from './Label';
import { Order } from '../models/APIModels';
import Moment from 'moment';
import { isEmptyValue } from '../utilities/ValueHelpers';

interface OrderSelectionModalProps {
  isOpen: boolean;
  orders: Order[];
  onSelectOrder: (order: Order) => void;
  onClose: () => void;
}

const customStyles = {
  content: {
    display: 'flex',
    maxWidth: '800px',
    margin: '0 auto',
  },
};

export function OrderSelectionModal({
  isOpen,
  orders,
  onSelectOrder,
  onClose,
}: OrderSelectionModalProps): JSX.Element {
  // Filter orders: storage orders without delivery dates, excluding suitcase orders
  const eligibleOrders = orders.filter(
    (order) =>
      order.storageRequired && // Only storage orders
      !order.suitcaseOrder && // Exclude suitcase orders
      isEmptyValue(order.deliveryDate), // Orders without delivery dates
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Select Order to Return"
    >
      <div className="order-selection-modal">
        <div className="modal-header">
          <h3>Select Order to Return</h3>
          <SubmitButton red={true} onClick={onClose}>
            <XMarkIcon width={18} height={18} />
          </SubmitButton>
        </div>

        <div className="modal-content">
          {eligibleOrders.length === 0 ? (
            <div className="no-orders">
              <p>No eligible orders found for return.</p>
              <p className="grey">
                Only storage orders that haven't been delivered yet can be
                returned.
              </p>
            </div>
          ) : (
            <>
              <p className="grey">
                Select the storage order you'd like to request delivery for:
              </p>
              <div className="orders-list">
                {eligibleOrders.map((order) => (
                  <div
                    key={order.id}
                    className="order-item"
                    onClick={() => onSelectOrder(order)}
                  >
                    <div className="order-info">
                      <div className="order-header">
                        <Label>Order #{order.id}</Label>
                        <span className="order-date">
                          {Moment(order.createdAt).format('MMM Do, YYYY')}
                        </span>
                      </div>
                      <div className="order-details">
                        <p>
                          <strong>Collection:</strong> {order.collectionAddress}
                        </p>
                        <p>
                          <strong>Collected:</strong>{' '}
                          {Moment(order.collectionDate).format(
                            'dddd, MMM Do YYYY',
                          )}
                        </p>
                        <p>
                          <strong>Items:</strong>{' '}
                          {order.orderBoxes?.length || 0} boxes in storage
                        </p>
                      </div>
                    </div>
                    <div className="select-arrow">
                      <ChevronRightIcon width={18} height={18} />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {eligibleOrders.length === 0 && (
          <SubmitButton onClick={onClose}>Close</SubmitButton>
        )}
      </div>
    </Modal>
  );
}
